import React, {useEffect, useState} from 'react'
import {OrderInfoDto, SubmitOrderRequestDto} from "../../../../utils/types";
import {useStores} from "../../../../store";
import {i18n} from "../../../../i18n";
import {Button} from "antd";
import {ButtonStyle} from "../../../../style/button";
import MedicationOrderTable from "./MedicationOrderTable";
import {useDownloadPdf} from "../../../../utils/hooks/useDownloadPdf";
import {observer} from "mobx-react";

const MedicationOrder: React.FC<MedicationOrderProps> = observer((props: MedicationOrderProps) => {
    const {patientStore, therapyStore, orderSummaryStore} = useStores()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [orders, setOrders] = useState<OrderInfoDto[] | null>(null)
    const [ordersLoading, setOrdersLoading] = useState(false)
    const {downloadStates, downloadPdf} = useDownloadPdf();

    const patientFirstName = patientStore.patientOverview?.patient.firstName
    const patientLastName = patientStore.patientOverview?.patient.lastName
    const therapyId = therapyStore.therapyComplete?.generalInfo.id

    const therapyPlanSuccessfulSubmitted = orderSummaryStore.getOrderSummary?.therapyPlanSuccesfulSubmitted

    useEffect(() => {
        const fetchOrders = async () => {
            if (!therapyId) {
                console.error('No therapy ID available')
                return
            }
            setOrdersLoading(true)
            try {
                const fetchedOrders = await therapyStore.getTherapyOrders(therapyId)
                setOrders(fetchedOrders)
            } catch (error) {
                console.error('Failed to fetch orders:', error)
            } finally {
                setOrdersLoading(false)
            }
        }

        fetchOrders()
    }, []);

    const handleSubmitOrder = async () => {
        if (!therapyId) {
            console.error('No therapy ID available')
            return
        }

        setIsSubmitting(true)

        try {
            const request: SubmitOrderRequestDto = {
                therapy_id: therapyId
            }
            await orderSummaryStore.submitTherapyOrder(request)

            props.onCloseModal()

        } catch (error) {
            console.error('Failed to submit therapy order:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            maxWidth: '100%'
        }}>
            {/* Header Section */}
            <div style={{marginBottom: '8px'}}>
                <div style={{marginBottom: '20px'}}>
                    <p style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: '0 0 4px 0'
                    }}>
                        {i18n.t('therapyTab.medicationOrder.firstLine', {
                            firstName: patientFirstName,
                            lastName: patientLastName
                        })}
                    </p>
                </div>

                {therapyPlanSuccessfulSubmitted ?
                    <div>
                        <p style={{margin: '0'}}>
                            {i18n.t('therapyTab.medicationOrder.orderSubmitted')}
                        </p>
                    </div> :
                    <div>
                        <p style={{margin: '0 0 12px 0'}}>
                            {i18n.t('therapyTab.medicationOrder.orderNotSubmitted')}
                        </p>
                        <p style={{
                            margin: '0',
                            fontWeight: '500'
                        }}>
                            {i18n.t('therapyTab.medicationOrder.orderNotSubmittedQuestion')}
                        </p>
                    </div>
                }
            </div>

            {/* Table Section */}
            <div style={{
                width: '100%',
                marginBottom: '16px'
            }}>

                <MedicationOrderTable
                    data={orders}
                    loading={ordersLoading}
                    onDownload={downloadPdf}
                    downloadStates={downloadStates}
                />
            </div>

            {/* Buttons Section */}
            <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                flexWrap: 'wrap' // Allow buttons to wrap on small screens
            }}>
                <Button
                    type={'default'}
                    shape={'round'}
                    style={ButtonStyle.primary}
                    onClick={props.onCloseModal}
                >
                    {therapyPlanSuccessfulSubmitted ? i18n.t('button.ok') : i18n.t('button.cancel')}
                </Button>
                {!therapyPlanSuccessfulSubmitted &&
                    <Button
                        type={'default'}
                        shape={'round'}
                        style={ButtonStyle.primary}
                        onClick={handleSubmitOrder}
                        disabled={isSubmitting || !therapyId}
                        loading={isSubmitting}
                    >
                        {isSubmitting ? i18n.t('button.submitting') : i18n.t('button.yes')}
                    </Button>
                }
            </div>
        </div>
    )
})

type MedicationOrderProps = {
    onCloseModal: () => void
}

export default MedicationOrder